"use client";

import Link from "next/link";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";

const TIER_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  free: {
    label: "Free",
    className: "bg-zinc-700/60 text-zinc-300",
  },
  starter: {
    label: "Starter",
    className: "bg-blue-600/20 text-blue-400 ring-1 ring-blue-500/30",
  },
  plus: {
    label: "Plus",
    className: "bg-violet-600/20 text-violet-400 ring-1 ring-violet-500/30",
  },
  pro: {
    label: "Pro",
    className: "bg-amber-600/20 text-amber-400 ring-1 ring-amber-500/30",
  },
  enterprise: {
    label: "Enterprise",
    className: "bg-emerald-600/20 text-emerald-400 ring-1 ring-emerald-500/30",
  },
};

/**
 * SubscriptionBadge — compact pill shown in the dashboard Navbar.
 *
 * Displays the user's current subscription tier and trial status.
 * Links to the subscription management page. Shows a "Trial" indicator
 * when the user is on a free trial, and an "Upgrade" prompt when on free tier.
 */
export function SubscriptionBadge() {
  const { isLoading, tier, isTrialing, hasAccess } = useSubscription();

  if (isLoading) {
    return (
      <div className="h-6 w-16 animate-pulse rounded-full bg-muted" />
    );
  }

  const config = TIER_CONFIG[tier] ?? TIER_CONFIG.free;

  // Free tier — show upgrade CTA
  if (tier === "free" && !isTrialing && !hasAccess) {
    return (
      <Link
        href="/pricing"
        className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary ring-1 ring-primary/30 transition-colors hover:bg-primary/25"
      >
        Upgrade
      </Link>
    );
  }

  return (
    <Link
      href="/dashboard/subscription"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80",
        config.className,
      )}
    >
      {config.label}
      {isTrialing && (
        <span className="rounded-full bg-white/10 px-1.5 py-px text-[10px] font-semibold uppercase leading-tight">
          Trial
        </span>
      )}
    </Link>
  );
}
